import { useTranslation } from "react-i18next";
import { Button, Col, Form, Row } from "react-bootstrap";
import { RootState } from "../../../../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { updateAnswer, updateQuestion } from "../../../../../../redux/surveySlice";
import { SurveyAnswer, SurveyQuestion } from "../../../types";
import { TranslateDescription } from "../../../../../../types/TranslateDescription";
import SurveyQuestionAnswerNPS from "./SurveyQuestionAnswerNPS";
import SurveyQuestionAnswerText from "./SurveyQuestionAnswerText";
import SurveyQuestionAnswerNumber from "./SurveyQuestionAnswerNumber";
import SurveyQuestionAnswerOption from "./SurveyQuestionAnswerOption";
import { AnswerTypeEnum } from "../../../types/enum";
import SurveyQuestionAnswerFormula from "./SurveyQuestionAnswerFormula";

interface SurveyQuestionFormProps {
    index: number;
    onPreview: ()=> void; 
}

export default function SurveyQuestionForm({ index, onPreview }: SurveyQuestionFormProps) {
    const { t } = useTranslation(['surveyAdmin', 'common']);
    const dispatch = useDispatch();
    const errors = useSelector((state: RootState) => state.surveyForm.errors);
    const question = useSelector((state: RootState) => state.surveyForm.data.questions[index]);
   
    const updateQuestionField = (index: number, field: keyof SurveyQuestion, value: any) => {
        dispatch(updateQuestion({ index, field, value }))
    }

    const updateQuestionMultilangueField = (index: number, language: keyof TranslateDescription, field: keyof SurveyQuestion, value: any) => {
        const currentTranslations = question[field] as TranslateDescription;
        dispatch(updateQuestion({ 
            index, 
            field, 
            value: { 
                ...currentTranslations, 
                [language]: value 
            } 
        }));
    }

    const updateAnswerField = (questionIndex: number, answerIndex: number, field: keyof SurveyAnswer, value: any) => {
        dispatch(updateAnswer({ questionIndex, answerIndex, field, value }))
    }

    const updateAnswerMultilangueField = (questionIndex: number, answerIndex: number, language: keyof TranslateDescription, field: keyof SurveyAnswer, value: any) => {
        const currentTranslations = question.answerOptions[answerIndex][field] as TranslateDescription;
        dispatch(updateAnswer({ 
            questionIndex,
            answerIndex,
            field, 
            value: { 
                ...currentTranslations, 
                [language]: value 
            } 
        }));
    }

    const updateAnswerAllMultilanguesField = (questionIndex: number, answerIndex: number, field: keyof SurveyAnswer, value: any) => {
        const newTranslations: TranslateDescription = {
            catalan: value,
            english: value,
            spanish: value,
            french: value
        };
        
        dispatch(updateAnswer({ 
            questionIndex,
            answerIndex,
            field, 
            value: newTranslations
        }));
    }

    return (
        <div>
            <Row className="mb-4">
                <Col>
                    <h3>{t("surveyAdmin:properties")}</h3>
                </Col>
                <Col className="d-flex justify-content-end">
                    <Button onClick={onPreview}>{t("survey:preview.previewButton")}</Button>
                </Col>
            </Row>
            <Form>
                <Row>
                    <Col md={9}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("surveyAdmin:questionText")} *</Form.Label>
                            <Form.Control
                                as="textarea"
                                isInvalid={!!errors[`questions[${index}].text.catalan`]}
                                value={question.text?.catalan ?? ""}
                                onChange={(e) => updateQuestionMultilangueField(index, 'catalan', 'text', e.target.value)}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors[`questions[${index}].text.catalan`]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={5}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("common:code")}</Form.Label>
                            <Form.Control
                                value={question.code ?? ""}
                                onChange={(e) => updateQuestionField(index, 'code', e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={7}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("common:description")}</Form.Label>
                            <Form.Control
                                value={question.description ?? ""}
                                onChange={(e) => updateQuestionField(index, 'description', e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={5}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("common:order")}</Form.Label>
                            <Form.Control
                                type="number" 
                                step="1"
                                min="0"
                                value={question.order ?? ""}
                                onChange={(e) => updateQuestionField(index, 'order', e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={9}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("surveyAdmin:helperText")}  *</Form.Label>
                            <Form.Control
                                isInvalid={!!errors[`questions[${index}].helperText.catalan`]}
                                value={question.helperText?.catalan ?? ""}
                                onChange={(e) => updateQuestionMultilangueField(index, 'catalan', 'helperText', e.target.value)}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors[`questions[${index}].helperText.catalan`]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={9}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t("surveyAdmin:responseType")}</Form.Label>
                            <Form.Select
                                as="select"
                                value={question.answerType ?? ""}
                                onChange={(e) => { updateQuestionField(index, 'answerType', e.target.value) }}
                            >
                                {Object.values(AnswerTypeEnum).filter((key) => isNaN(Number(key))).map((name) => {
                                    const value = AnswerTypeEnum[name as keyof typeof AnswerTypeEnum];
                                    return (<option key={value} value={value}>
                                        {t(`surveyAdmin:form.answerType.${name}`)} 
                                    </option>)
                                })}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Form.Check
                            type="switch"
                            label={t("surveyAdmin:answerMandatory")}
                            checked={question.isAnswerMandatory}
                            onChange={(e) => { updateQuestionField(index, 'isAnswerMandatory', e.target.checked) }}
                        />
                    </Col>
                    {question.answerType == AnswerTypeEnum.Formula &&
                        <Col md={6}>
                            <Form.Check
                                type="switch"
                                label={t("surveyAdmin:form.showFormulaInSurvey")}
                                checked={question.showFormulaInSurvey}
                                onChange={(e) => { updateQuestionField(index, 'showFormulaInSurvey', e.target.checked) }}
                            />
                        </Col>
                    }
                </Row>
                {
                    question.answerType == AnswerTypeEnum.NPS && (
                        <SurveyQuestionAnswerNPS index={index} updateQuestionField={updateQuestionField} />
                    )
                }
                {
                    question.answerType == AnswerTypeEnum.Text && (
                        <SurveyQuestionAnswerText index={index} updateQuestionField={updateQuestionField} />
                    )
                }
                {
                    question.answerType == AnswerTypeEnum.Number && (
                        <SurveyQuestionAnswerNumber index={index} updateQuestionField={updateQuestionField} />
                    )
                }
                {
                    question.answerType == AnswerTypeEnum.Option && (
                        <SurveyQuestionAnswerOption 
                            index={index}
                            updateQuestionField={updateQuestionField}
                            updateAnswerField={updateAnswerField}
                            updateAnswerMultilangueField={updateAnswerMultilangueField} 
                            updateAnswerAllMultilanguesField={updateAnswerAllMultilanguesField}
                        />
                    )
                }
                {
                    question.answerType == AnswerTypeEnum.Formula && (
                        <SurveyQuestionAnswerFormula index={index} updateQuestionField={updateQuestionField} />
                    )
                }
            </Form>
        </div>
    );
}